import type { AxiosInstance } from "axios";
import { stringify } from "query-string";
import type { CrudFilters, DataProvider } from "@refinedev/core";
import { generateFilter, generateSort } from "@refinedev/simple-rest";

import { axiosInstance } from "@desk/utils";
import { lowerCase } from "lodash-es";

const url = import.meta.env.VITE_API_URL;

type MethodTypes = "get" | "delete" | "head" | "options";
type MethodTypesWithBody = "post" | "put" | "patch";

export const dataProvider = (
  apiUrl: string,
  httpClient: AxiosInstance = axiosInstance
): Omit<
  Required<DataProvider>,
  "createMany" | "updateMany" | "deleteMany"
> => ({
  getList: async ({ resource, pagination, filters, meta }) => {
    const url = `/${resource}`;

    const { pageSize = 10, mode = "server" } = pagination ?? {};

    const { headers: headersFromMeta, method, status } = meta ?? {};
    const requestMethod = (lowerCase(method) as MethodTypes) || "get";

    const queryFilters = generateFilter(filters);

    const query: {
      page_size?: number;
      page?: string;
      status?: string;
    } = {};

    if (mode === "server") {
      query.page = pagination?.current
        ? String(pagination?.current)
        : undefined;
      query.page_size = pageSize;
      query.status = status;
    }

    const { data, headers } = await httpClient[requestMethod](`${url}`, {
      headers: headersFromMeta,
      params: { ...query, ...queryFilters },
    });

    for (const key in data) {
      if (Array.isArray(data[key])) {
        Object.assign(data, { data: data[key] });
        delete data[key];
        break;
      }
    }

    return {
      data: data.data,
      total: data.total,
      total_pages: data.total_pages ?? 1,
    };
  },

  getMany: async ({ resource, ids, meta }) => {
    const { headers, method } = meta ?? {};
    const requestMethod = (lowerCase(method) as MethodTypes) || "get";

    const { data } = await httpClient[requestMethod](
      `/${resource}?${stringify({ id: ids })}`,
      { headers }
    );

    return {
      data,
    };
  },

  create: async ({ resource, variables, meta }) => {
    const url = `/${resource}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (lowerCase(method) as MethodTypesWithBody) || "post";

    const { data } = await httpClient[requestMethod](url, variables, {
      headers,
    });

    return {
      data,
    };
  },

  update: async ({ resource, id, variables, meta }) => {
    const url = `/${resource}/${id}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (lowerCase(method) as MethodTypesWithBody) || "patch";

    const { data } = await httpClient[requestMethod](url, variables, {
      headers,
    });

    return {
      data,
    };
  },

  getOne: async ({ resource, id, meta }) => {
    const url = `/${resource}/${id}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (lowerCase(method) as MethodTypes) || "get";

    const { data } = await httpClient[requestMethod](url, { headers });

    return {
      data,
    };
  },

  deleteOne: async ({ resource, id, variables, meta }) => {
    const url = `/${resource}/${id}`;

    const { headers, method } = meta ?? {};
    const requestMethod = (method as MethodTypesWithBody) || "delete";

    const { data } = await httpClient[requestMethod](url, {
      data: variables,
      headers,
    });

    return {
      data,
    };
  },

  getApiUrl: () => {
    return apiUrl;
  },

  custom: async ({
    url,
    method,
    filters,
    sorters,
    payload,
    query,
    headers,
  }) => {
    let requestUrl = `${url}?`;

    if (sorters) {
      const generatedSort = generateSort(sorters);
      if (generatedSort) {
        const { _sort, _order } = generatedSort;
        const sortQuery = {
          _sort: _sort.join(","),
          _order: _order.join(","),
        };
        requestUrl = `${requestUrl}&${stringify(sortQuery)}`;
      }
    }

    if (filters) {
      const filterQuery = generateFilter(filters);
      requestUrl = `${requestUrl}&${stringify(filterQuery)}`;
    }

    if (query) {
      requestUrl = `${requestUrl}&${stringify(query)}`;
    }

    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
      } as any;
    }

    let axiosResponse;
    switch (method) {
      case "put":
      case "post":
      case "patch":
        axiosResponse = await httpClient[method](url, payload);
        break;
      case "delete":
        axiosResponse = await httpClient.delete(url, {
          data: payload,
        });
        break;
      default:
        axiosResponse = await httpClient.get(requestUrl);
        break;
    }

    const { data } = axiosResponse;

    return Promise.resolve({ data });
  },
});

export default dataProvider(url, axiosInstance);
